import React, { CSSProperties } from 'react';
import { areEqual } from 'react-window';

import CentralSwitchOverviewRow from './CentralSwitchOverviewRow';
import ContactListEntityRow from './ContactListEntityRow';
import FoldableSectionTitle from 'flow/components/nav/list-items/FoldableSectionTitle';
import ListRowFrame from 'flow/components/nav/list-keys/ListRowFrame';
import ConnectedServicesInstructions from './ConnectedServicesInstructions';
import { ContactListItemData } from './contact-list-item-data';
import { rowIsFocusable } from './contact-list-rows';

interface Props {
    index: number;
    style: CSSProperties;
    data: ContactListItemData;
}

// A row's content renders again only when its own props change, not with
// every change to the list's rows. The frame and RowContent render again;
// the content takes values and callbacks that keep their identity.
const SectionTitle = React.memo(FoldableSectionTitle);
const Instructions = React.memo(ConnectedServicesInstructions);

const RowContent = ({ index, data }: Omit<Props, 'style'>) => {
    const row = data.rows[index];

    switch (row.kind) {
        case 'centralSwitchOverview':
            return <CentralSwitchOverviewRow index={index} data={data} />;
        case 'sectionTitle':
            return (
                <SectionTitle
                    sectionId={row.sectionId}
                    title={row.title}
                    isOpen={row.isOpen}
                    canToggle={row.canToggle}
                    onToggle={data.onToggleSection}
                />
            );
        case 'extension':
        case 'externalContact':
        case 'thirdPartyContact':
            return (
                <ContactListEntityRow
                    index={index}
                    kind={row.kind}
                    entityKey={row.entityKey}
                    data={data}
                />
            );
        case 'connectedServicesInstructions':
            return <Instructions />;
        case 'unknownKey':
            return row.key;
        default:
            return null;
    }
};

const ContactListRow = ({ index, style, data }: Props) => (
    <ListRowFrame
        index={index}
        style={style}
        isFocusable={rowIsFocusable(data.rows[index])}
        tabStopStore={data.tabStopStore}
        tabStopKey={data.rowKeys[index]}
        onUnmountWithFocus={data.onRowUnmountWithFocus}
    >
        <RowContent index={index} data={data} />
    </ListRowFrame>
);

export default React.memo(ContactListRow, areEqual);
